import { useMemo, useState, memo, useEffect } from 'react';
import View from '@picsart/view/View';
import PaImage from '@picsart/image/Image';
import IconArrowRightLarge from '@picsart/icons/IconArrowRightLarge';
import { useTranslator } from '@picsart/rc/services/localization';

import useMemoSelector from 'hooks/useMemoSelector';
import { getBatchActiveColor, getBatchActiveTexture } from 'store/batch/selectors';
import { getTargetSizes } from 'pages/Batch/utils/process/resizeProcess';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';

import useStyles from './styles';

export interface IRbGridItem {
  imgSrc: string;
}

const RemoveBgResizeGridItem = ({ imgSrc }: IRbGridItem) => {
  const translate = useTranslator();

  const { activeColor, activeTexture } = useMemoSelector(state => ({
    activeColor: getBatchActiveColor(state),
    activeTexture: getBatchActiveTexture(state),
  }));

  const { resizeParams } = useBatchData();
  const [imgSizes, setImgSizes] = useState([0, 0]);

  const [resizedWidth, resizedHeight] = useMemo(() => {
    const width = resizeParams.isOriginal ? imgSizes[0] : resizeParams.width;
    const height = resizeParams.isOriginal ? imgSizes[1] : resizeParams.height;
    return getTargetSizes(imgSizes[0], imgSizes[1], width, height);
  }, [resizeParams, imgSizes]);

  const isMaxWidth = useMemo(() => resizedWidth > resizedHeight, [resizedWidth, resizedHeight]);
  const scale = useMemo(() => (isMaxWidth ? resizedHeight / resizedWidth : resizedWidth / resizedHeight) || 0, [isMaxWidth, resizedWidth, resizedHeight]);
  const classes = useStyles({
    resizeParams: {
      ...resizeParams,
      width: resizeParams.width || imgSizes[0],
      height: resizeParams.height || imgSizes[1],
    },
    resizedWidth,
    resizedHeight,
    scale,
    isMaxWidth,
    bgColor: activeColor,
  });

  useEffect(() => {
    const img = new Image();
    img.src = imgSrc;
    img.onload = () => {
      setImgSizes([img.width, img.height]);
    };
  }, [imgSrc]);

  return (
    <View className={classes.resizeItemContainer}>
      <View className={classes.resizeItem}>
        {activeTexture && (
          <PaImage
            src={activeTexture?.url}
            alt="img"
            className={classes.gridImage}
          />
        )}

        <PaImage src={imgSrc} alt="img" className={classes.resizeImg} />
      </View>

      <View className={classes.titleContainer}>
        <p className={classes.sizeTitle}>
          {translate('{{sizeX}} x {{sizeY}}', {
            sizeX: imgSizes[0],
            sizeY: imgSizes[1],
          })}
        </p>

        {(imgSizes[0] !== resizedWidth || imgSizes[1] !== resizedHeight) && (
          <>
            <IconArrowRightLarge width="20px" height="12px" fill="#fff" />

            <p className={classes.sizeTitle}>
              {translate('{{resizedWidth}} x {{resizedHeight}}', {
                resizedWidth,
                resizedHeight,
              })}
            </p>
          </>
        )}
      </View>
    </View>
  );
};

export default memo(RemoveBgResizeGridItem);
